import { defineStore } from 'pinia';
import { useNotificationsStore } from '@/stores/useNotifications';
import { notificationKeys } from '@/constants/notificationKeys';

export const useUserDetailsStore = defineStore('userDetailsStore', {
  state: () => {
    return {
      user: null,
      // UI-only: whether the TMS edit sheet is open. Lives here so the walkthrough setup
      // in walkthroughSetups.js can reach it from outside the page's component tree.
      isTmsSheetOpen: false,
    };
  },
  actions: {
    async getUserDetails() {
      let user = localStorage.getItem('user');
      if (user) {
        user = JSON.parse(user);
        this.user = user.data;
        localStorage.removeItem('user');
        return;
      }
      const { data, pending, error } = await useAcvFetch(`api/user/userdetails`);

      if (error.value) {
        return {
          status: error.value.statusCode,
        };
      }
      this.user = data.value.data;
      return { user: data.value.data, pending };
    },

    async updateTmsName(tmsName) {
      const { $api } = useNuxtApp();
      const { addRumError } = useAcvDatadog();
      const notificationsStore = useNotificationsStore();
      const tmsValueMap = {
        ACV: 'acv',
        'Super Dispatch': 'super_dispatch',
      };
      let data = null;
      let error = null;

      try {
        data = await $api('/api/carrier/', {
          method: 'POST',
          body: { preferredTMS: tmsValueMap[tmsName] },
        });
      } catch (err) {
        error = err;
      }

      if (error) {
        const customError = new Error('500 update tms');
        addRumError(customError, { tmsName });

        const errorMessage = error.data?.errors?.[0] || 'Unable to update preferred TMS.';
        notificationsStore.addNotification({
          notificationMessageTemplate: `<span class="tw-font-medium">${errorMessage}</span>`,
          variant: 'error',
          key: notificationKeys.errorUpdateTms,
        });
        return error;
      }

      this.user.carrier.preferredTMS = data.data?.preferredTMS ?? tmsValueMap[tmsName];
      notificationsStore.addNotification({
        notificationMessageTemplate: 'Preferred TMS successfully updated.',
        variant: 'success',
        key: notificationKeys.successUpdateTms,
      });
      return { data: data.data };
    },

    async getAuthenticatedUserDetails() {
      // need to check a specific value since even on an error, we populate some user data

      if (this.user?.auth0Id) {
        return { carrierName: this.user.carrier.name };
      }

      const { data, error } = await useAcvFetch(`api/user/authenticate`);

      if (error.value) {
        return {
          carrierName: error.value?.data?.errors?.[0]?.carrierName,
          status: error.value?.statusCode,
        };
      }

      this.user = data.value.data;
      return { user: data.value.data };
    },

    /**
     * Account for vehicles staged or unstaged during this session, since currentJobCount is
     * only ever fetched fresh on login and won't otherwise reflect actions taken after that.
     * @param {Number} vehicleCount - vehicles newly staged (positive) or unstaged (negative)
     */
    adjustCurrentJobCountForStagedVehicles(vehicleCount) {
      this.user.carrier.currentJobCount += vehicleCount;
    },

    async completeOnboardingWalkthrough() {
      if (this.user?.hasCompletedOnboardingWalkthrough) {
        return;
      }
      const { $api } = useNuxtApp();
      const { addRumError } = useAcvDatadog();

      try {
        await $api('/api/user/completedwalkthrough', { method: 'POST' });
      } catch (error) {
        addRumError(new Error('Failed to mark onboarding walkthrough as completed'), { error });
        return;
      }

      this.user.hasCompletedOnboardingWalkthrough = true;
    },
  },
  getters: {
    carrier: (state) => state.user?.carrier,
    role: (state) => state.user?.role,
    userName: (state) => state.user?.userName,
    userID: (state) => state.user?.transUserId,
    carrierID: (state) =>
      state.user?.carrier?.sfId ? state.user?.carrier?.sfId : state.user?.sfId, // adding this conditional to guard against the case where the carrier object has NOT been linked to the user
    currentUserInitials: (state) => state.user?.userName.slice(0, 1),
    companyName: (state) => state.user?.name,
    maxJobCount: (state) => state.user?.carrier?.maxJobCount,
    currentJobCount: (state) => state.user?.carrier?.currentJobCount,
    postalCode: (state) => state.user?.carrier?.postalCode,
    email: (state) => state.user?.email,
    termsAccepted: (state) => state.user?.termsAccepted,
    amName: (state) => state.user?.carrier?.amName,
    amPhone: (state) => state.user?.carrier?.amPhone,
    amEmail: (state) => state.user?.carrier?.amEmail,
    accountLevel: (state) => state.user?.carrier.accountLevel,
    phone: (state) => state.user?.phone,
    tmsName: (state) => state.user?.carrier?.preferredTMS,
    isFullySignedUp: (state) =>
      Boolean(state.user?.role) && state.user?.role !== 'New' && state.user?.termsAccepted === true,
  },
});
